import React, { useEffect, useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import { ODataOptions, ODataFilter, ODataFilterOperators, ODataOrderByDirection } from 'src/util/ODataBuilder';
import Client from 'src/models/AP/Client';
import { ITableConfig } from 'src/components/common/table';
import useFetch from 'use-http';
import _ from 'lodash';
import TsdiClient from 'src/models/AP/Client';
import 'src/components/client-project/dialogs/FindClientDialog.css';

interface IFindClientDialogProps {
    dialogName: string;
    hideDialog: () => void;
    onCancel?: () => void;
    onAddNewClient?: (payload: TsdiClient) => void;
    onDuplicateClientName?: (payload: TsdiClient) => void;
    resetClientSearchOptions?: () => void;
    setClientSearchTerm?: (keyword: string) => void;
    incrementClients?: () => void;
    clients?: Client[];
    showAddVendorDialog?: () => void;
    setSelectedClient?: (clientNumber: TsdiClient) => void;
    style?: React.CSSProperties;
    addNewClient?: boolean;
    defaultClient?: number;
    mileageRate?: number;
    clientTypes?: { code: number, description: string}[];
    invoiceTypes?: { code: number, description: string}[];
}

const FindClientDialog = (props: IFindClientDialogProps) => {
    const { register, errors, getValues, handleSubmit } = useForm();
    const [fetchNextPage, setFetchNextPage] = useState<boolean>(true);
    const [clientSearchOptions, setClientSearchOptions] = useState<ODataOptions>(undefined);
    const [billByOptions, setBillByOptions] = React.useState<Array<{ value: number, label: string}>>(undefined);
    const [clientSearchTerm, setClientSearchTerm] = useState<string>('');
    const [clients, setClients] = useState<Array<TsdiClient>>(undefined);
    const [selectedClient, setSelectedClient] = useState<number>(null);
    const { get, response } = useFetch();
    // const dispatch = useDispatch();

    useEffect(() => {
        onCreate();
    }, []);

    useEffect(() => {
        if (!!clientSearchOptions) {
            getClients();
        }
    }, [clientSearchOptions]);

    useEffect(() => {
        if ((!!clientSearchTerm || clientSearchTerm === '') && !!clientSearchOptions) {
            resetClientSearchOptions();
        }
    }, [clientSearchTerm]);

    const onCreate = () => {        
        initializeClientSearchSettings();
        getBillbyOptions();
    };

    const resetState = () => {
        setSelectedClient(props?.defaultClient);
        setClientSearchTerm('');
        resetClientSearchOptions();
        setClients([]);
    };

    const OKClicked = () => {
        props.setSelectedClient(clients[selectedClient]);
        props.hideDialog();
        resetState();
    };

    const onClientSearchChanged = (searchContent: string) => {
        search();
    };

    const onCancel = () => {
        props.hideDialog();        
        resetState();
    };

    const initializeClientSearchSettings = () => {
        const tableConfig: ITableConfig = {
            rowCount: 100,
            idField: 'CL_NO',
            pageNumber: 1,
            columns: []
        };
        setClientSearchOptions(ODataOptions.from(tableConfig).addOrderBy('ClientName', ODataOrderByDirection.Ascending));
    };

    const getScrollStatus = (divElement: HTMLDivElement) => {
        if (divElement) {
            const scrollTop = divElement.scrollTop % 1 > .9 ? divElement.scrollTop : Math.round(divElement.scrollTop);
            const scrollHeight = divElement.scrollHeight;
            const clientHeight = divElement.clientHeight;

            // Check if reached the end of the list
            if (scrollTop + clientHeight >= scrollHeight) {
                incrementClients();
                console.log('end of scroll YES');
            } else {
                console.log('end of scroll NO');
            }
        }
    };

    const incrementClients = () => {
        if (!!fetchNextPage) {
            let newSearchOptions = _.cloneDeep(clientSearchOptions);
            newSearchOptions.incrementPageNumber();
            setClientSearchOptions(newSearchOptions);
        }
    };

    const selectClientAndClose = (client: TsdiClient) => {
        props.setSelectedClient(client);
        props.hideDialog();
        resetState();
    };

    const resetClientSearchOptions = () => {
        setFetchNextPage(true);
        let newClientSearchOptions = _.cloneDeep(clientSearchOptions);
        newClientSearchOptions.clearFilters();
        newClientSearchOptions.addPaging(1, 50);
        const fieldValues = clientSearchTerm.split(',');
        let addFilters = false;
        for (let fvIndex = 0; fvIndex < fieldValues.length; fvIndex++) {
            if (!!fieldValues[fvIndex] && fieldValues[fvIndex] !== '') {
                addFilters = true;
                break;
            }
        }
        if (!!fieldValues && fieldValues.length > 0 && !!addFilters) {
            if (!!fieldValues[0] && fieldValues[0] !== ' ') {
                newClientSearchOptions.addFilter(new ODataFilter('ClientNumber', ODataFilterOperators.Equals, !!fieldValues[0] ? fieldValues[0] : ''));
            }
            if (!!fieldValues[0] && fieldValues[1] !== ' ') {
                newClientSearchOptions.addFilter(new ODataFilter('ClientName', ODataFilterOperators.Contains, !!fieldValues[1] ? fieldValues[1] : ''));
            }
            if (!!fieldValues[0] && fieldValues[2] !== ' ') {
                newClientSearchOptions.addFilter(new ODataFilter('Address', ODataFilterOperators.Contains, !!fieldValues[2] ? fieldValues[2] : ''));
            }
            if (!!fieldValues[0] && fieldValues[3] !== ' ') {
                newClientSearchOptions.addFilter(new ODataFilter('CITY', ODataFilterOperators.Contains, !!fieldValues[3] ? fieldValues[3] : ''));
            }
            if (!!fieldValues[0] && fieldValues[4] !== ' ') {
                newClientSearchOptions.addFilter(new ODataFilter('STATE', ODataFilterOperators.Contains, !!fieldValues[4] ? fieldValues[4] : ''));
            }
            if (!!fieldValues[0] && fieldValues[5] !== ' ') {
                newClientSearchOptions.addFilter(new ODataFilter('ZIP', ODataFilterOperators.Contains, !!fieldValues[5] ? fieldValues[5] : ''));
            }
            if (!!fieldValues[0] && fieldValues[6] !== ' ') {
                newClientSearchOptions.addFilter(new ODataFilter('PHONE', ODataFilterOperators.Contains, !!fieldValues[6] ? fieldValues[6] : ''));
            }
        }
        setClientSearchOptions(newClientSearchOptions);
    };

    const getClients = async () => {
        const queryString = clientSearchOptions?.toString();
        let result = await get(`/v1.0/AccountsPayable/clients${queryString}`);

        if (response.ok) {
            if (clientSearchOptions?.getPageNumber() > 1) {
                let tempResult = _.cloneDeep(clients);
                setClients([]);
                tempResult = [...tempResult].concat([...result.items]);
                setClients(tempResult);
            } else {
                setClients([]);
                setClients(result.items);
            }
            setFetchNextPage(result.pageNumber * result.pageSize < result.totalRecords);
        } else {
            console.log('Unable to get client data!');
        }
    };

    const invoiceTypeDropdownOptions = () => {
        return props?.invoiceTypes
            ? props?.invoiceTypes?.map((it: { code: number, description: string}) => {
                return { value: it.code, label: it?.description };
            })
            : [];
    };

    const clientTypeDropdownOptions = () => {
        return props?.invoiceTypes
            ? props?.clientTypes?.map((it: { code: number, description: string}) => {
                return { value: it.code, label: it?.description };
            })
            : [];
    };

    const getBillbyOptions = async () => {
        const result = await get(`/v1.0/ClientProjectMaintenance/billing/billby/options`);

        if (response.ok) {
            if (!!result && result.length > 0) {
                setBillByOptions(result?.map((bbo: { code: number, description: string}) => {
                    return { value: bbo.code, label: bbo.description };
                }));
            }
        }
    };

    const search = _.debounce(() => {
        let searchString =  (!!getValues()?.searchClientNumber ? getValues().searchClientNumber : ' ');
        searchString += ',' + (!!getValues()?.searchClientName ? getValues().searchClientName : ' ');
        searchString += ',' + (!!getValues()?.searchClientCity ? getValues().searchClientCity : ' ');
        searchString += ',' + (!!getValues()?.searchClientState ? getValues().searchClientState : ' ');
        searchString += ',' + (!!getValues()?.searchClientZip ? getValues().searchClientZip : ' ');
        searchString += ',' + (!!getValues()?.searchAreaCode ? getValues().searchAreaCode : ' ');
        searchString += ',' + (!!getValues()?.searchProspect ? getValues().searchProspect : ' ');
        setClientSearchTerm(searchString);
        // props.resetClientSearchOptions();
    }, 800);

    const buildClientPayloadFromForm = () => {
        const payload = getValues();
        const transformedPayload = new TsdiClient();
        transformedPayload.clientName = payload.searchClientName;
        transformedPayload.backupOfficeRate = !isNaN(Number(payload.backupOfficeRate)) ? Number(payload.backupOfficeRate) : 250.00;
        transformedPayload.mileageRate = !isNaN(Number(payload.mileageRate)) ? Number(payload.mileageRate) : .7;
        transformedPayload.dayRate = !isNaN(Number(payload.defaultDayRate)) ? Math.round(Number(payload.defaultDayRate)) : 300;
        transformedPayload.formatType = !!payload?.clientType ? payload.clientType : 'L';
        transformedPayload.invoiceType = !isNaN(Number(payload.invoiceType)) ? Number(payload.invoiceType) : 1;
        transformedPayload.billBy = !!payload?.billBy ? payload.billBy : 'D';
        return transformedPayload;
    };

    const onCreateClient = (data: Object) => {
        console.log(data);
        const transformedPayload = buildClientPayloadFromForm();

        // The "Existing Clients" table below is already live-filtered by this same name (via the
        // Name field's onChange -> search()), so it's already narrowed to name-contains matches -
        // an exact (case-insensitive) match among them means a client with this identical name
        // already exists. Warns (with the option to proceed anyway) instead of silently allowing a
        // duplicate.
        const enteredName = (transformedPayload.clientName || '').trim().toLowerCase();
        const isDuplicateName = !!enteredName && clients?.some(
            (c) => (c.clientName || '').trim().toLowerCase() === enteredName
        );
        if (isDuplicateName) {
            props.onDuplicateClientName?.(transformedPayload);
            return;
        }

        props.onAddNewClient(transformedPayload);
    };

    // In Add Client mode, the "Existing Clients" table is a live name-filtered list - a row shown
    // there is already evidence of a name conflict, so double-clicking one should warn the same way
    // Save does, rather than falling through to Find-mode's selectClientAndClose (which doesn't make
    // sense while adding a new client). The pending payload still comes from the form's own current
    // values, not the clicked row, since that's what would actually be created if confirmed.
    const handleRowDoubleClick = (client: TsdiClient) => {
        if (props.addNewClient) {
            props.onDuplicateClientName?.(buildClientPayloadFromForm());
            return;
        }
        selectClientAndClose(client);
    };

    return (
        <>
        <Dialog title={!!props.addNewClient ? 'Add Client' : 'Find Client'} dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ maxHeight: '75vh', width: '50%' }}>
        <form id={'createClientForm'} onSubmit={handleSubmit(onCreateClient)}>
            <div style={{display: 'inline-block', marginTop: '1rem'}}>
                <div>
                {!props.addNewClient && <RHFInputBox
                    onChange={(event) => onClientSearchChanged(event.currentTarget.value)}
                    labelOnLeft={true}
                    label="Client #"
                    name={'searchClientNumber'}
                    style={{width: '40px'}}
                    register={register}
                />}
                </div>
                <RHFInputBox
                    onChange={(event) => onClientSearchChanged(event.currentTarget.value)}
                    labelOnLeft={true}
                    label="Name:"
                    name={'searchClientName'}
                    style={{width: '200px'}}
                    register={register}
                    errors={errors}
                    rules={{ required: ' Client Name required' }}
                    isRequired={true}
                />
                {!!props.addNewClient && <RHFSelect
                    bodyStyle={{marginBottom: '5px', whiteSpace: 'nowrap', width: '90%' }}
                    style={{whiteSpace: 'nowrap', width: '100%' }}
                    name={'clientType'}
                    label="Client Type:"
                    options={clientTypeDropdownOptions()}
                    register={register}
                    defaultVal={'L'}
                    errors={errors}
                    rules={{ required: ' Client Type required' }}
                    isRequired={true}
                />}
                {!!props.addNewClient && <RHFSelect
                    bodyStyle={{marginBottom: '5px', whiteSpace: 'nowrap', width: '90%' }}
                    style={{whiteSpace: 'nowrap', width: '100%' }}
                    name={'invoiceType'}
                    label="Initial Invoice Type:"
                    options={invoiceTypeDropdownOptions()}
                    defaultVal={'1'}
                    register={register}
                    errors={errors}
                    rules={{ required: ' Invoice Type required' }}
                    isRequired={true}
                />}
                {!!props.addNewClient && <RHFSelect
                    bodyStyle={{marginBottom: '5px', whiteSpace: 'nowrap', width: '90%' }}
                    style={{whiteSpace: 'nowrap', width: '100%' }}
                    name={'billBy'}
                    label="Bill By Type:"
                    options={billByOptions}
                    defaultVal={'D'}
                    register={register}
                    errors={errors}
                    rules={{ required: ' Bill by required' }}
                    isRequired={true}
                />}
                {!!props.addNewClient && <RHFInputBox
                    bodyClassName={'form-group'}
                    label={'Default Day Rate:'}
                    labelOnLeft={true}
                    name={'defaultDayRate'}
                    style={{whiteSpace: 'nowrap', width: '50%'}}
                    bodyStyle={{whiteSpace: 'nowrap', display: 'flex', alignItems: 'flex-end', width: '55%'}}
                    register={register}
                    rules={{ required: 'Day Rate required' }}
                    isRequired={true}
                    errors={errors}
                />}
                {!!props.addNewClient && <RHFInputBox
                    labelOnLeft={true}
                    label="Backup Office Rate:"
                    defaultVal={'250'}
                    name={'backupOfficeRate'}
                    style={{whiteSpace: 'nowrap', display: 'flex', alignItems: 'baseline', width: '50%'}}
                    bodyStyle={{whiteSpace: 'nowrap', display: 'flex', alignItems: 'baseline', width: '55%'}}
                    register={register}
                    isRequired={true}
                    errors={errors}
                    rules={{ required: 'Backup Rate required' }}
                />}
                {!!props.addNewClient && <RHFInputBox
                    labelOnLeft={true}
                    label="Mileage Rate:"
                    name={'mileageRate'}
                    defaultVal={props?.mileageRate?.toString()}
                    style={{width: '40px'}}
                    register={register}
                    isRequired={true}
                    errors={errors}
                    rules={{ required: 'Mileage Rate required' }}
                />}
                {!props.addNewClient && <RHFInputBox
                    onChange={(event) => onClientSearchChanged(event.currentTarget.value)}
                    labelOnLeft={true}
                    label="City"
                    name={'searchClientCity'}
                    style={{width: '200px'}}
                    register={register}
                />}
                {!props.addNewClient && <div style={{display: 'flex'}}>
                    <RHFInputBox
                        onChange={(event) => onClientSearchChanged(event.currentTarget.value)}
                        labelOnLeft={true}
                        label="State"
                        name={'searchClientState'}
                        style={{width: '20px'}}
                        register={register}
                    />
                    <RHFInputBox
                        onChange={(event) => onClientSearchChanged(event.currentTarget.value)}
                        labelOnLeft={true}
                        label="Zip"
                        name={'searchClientZip'}
                        style={{width: '50px'}}
                        register={register}
                    />
                </div>}
                {!props.addNewClient && <RHFInputBox
                    onChange={(event) => onClientSearchChanged(event.currentTarget.value)}
                    labelOnLeft={true}
                    label="Area Code"
                    name={'searchAreaCode'}
                    style={{width: '30px'}}
                    register={register}
                />}
                {!props.addNewClient && <RHFInputBox
                    onChange={(event) => onClientSearchChanged(event.currentTarget.value)}
                    labelOnLeft={true}
                    label="Prospect Name"
                    name={'SearchProspect'}
                    style={{width: '200px'}}
                    register={register}
                />}
            </div>
            <div style={{marginTop: '3rem', overflowY: 'hidden', maxHeight: '200px'}}>
                    {props.addNewClient && <div style={{fontWeight: 'bold', fontFamily: 'Arial', width: '100%', textAlign: 'center'}}>
                        Existing Clients
                    </div>}
                    <table className={'project-billing'}>
                        <thead className={'project-billing'} style={{ textAlign: 'left' }}>
                            <tr>
                                <th>#</th>
                                <th>Name</th>
                                <th>Address</th>
                                <th>City</th>
                                <th>State</th>
                            </tr>
                        </thead>
                        <tbody className={'project-billing'} onScroll={(event) => getScrollStatus(event.currentTarget)}>
                        {clients && clients?.map((client: Client, index: number) => {
                            return (
                                    // tslint:disable-next-line
                                    <tr key={client.clientNumber + index} onDoubleClick={() => handleRowDoubleClick(client)} onClick={() => setSelectedClient(index) } className="table-item-hover" style={{ textAlign: 'left' }}>
                                        <td style={{ width: '7%' }}>
                                            <RHFInputBox
                                                name={`clientNumbervendors[${index}].vendorNumber`}
                                                type="text"
                                                defaultVal={client.clientNumber + ''}
                                                register={register}
                                                errors={errors}
                                                style={{ width: '100%' }}
                                                bodyStyle={{ width: '100%' }}
                                            />
                                        </td>
                                        <td style={{ width: '30%' }}>
                                            <RHFInputBox
                                                name={`vendors[${index}].vendorName`}
                                                type="text"
                                                defaultVal={client.clientName}
                                                register={register}
                                                errors={errors}
                                                style={{ width: '100%' }}
                                                bodyStyle={{ width: '100%' }}
                                            />
                                        </td>
                                        <td style={{ width: '32%' }}>
                                            <RHFInputBox
                                                name={`vendors[${index}].address`}
                                                type="text"
                                                defaultVal={client.address}
                                                register={register}
                                                errors={errors}
                                                style={{ width: '100%' }}
                                                bodyStyle={{ width: '100%' }}
                                            />
                                        </td>
                                        <td style={{ width: '15%' }}>
                                            <RHFInputBox
                                                name={`vendors[${index}].city`}
                                                type="text"
                                                defaultVal={client.city}
                                                register={register}
                                                errors={errors}
                                                style={{ width: '100%' }}
                                                bodyStyle={{ width: '100%' }}
                                            />
                                        </td>
                                        <td style={{ width: '15%' }}>
                                            <RHFInputBox
                                                name={`vendors[${index}].state`}
                                                type="text"
                                                defaultVal={client.state}
                                                register={register}
                                                errors={errors}
                                                style={{ width: '100%' }}
                                                bodyStyle={{ width: '100%' }}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="project-billing form-row" style={{marginTop: '2rem', marginLeft: '33%', textAlign: 'center'}}>
                {!!props.addNewClient && <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'Save New Client'}
                    type={'submit'}
                    form={'createClientForm'}
                />}
                {!props.addNewClient && <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'OK'}
                    onClick={() => OKClicked()}
                />}
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'Cancel'}
                    onClick={() => onCancel()}
                />
                </div>
            </form>

        </Dialog>
        </>
    );
};

export default FindClientDialog;



// WEBPACK FOOTER //
// ./src/components/client-project/dialogs/FindClientDialog.tsx